import { Card } from "../ui";
import type { CardProps } from "../ui";
import type { CSSProperties, ReactNode } from "react";
import { spacing } from "../settings/settingsListDesign";

export type SectionCardBaseProps = CardProps & { padding?: "default" | "compact"; compact?: boolean; flush?: boolean };

/** Complete member card contract. Existing member props remain supported. */
export function SectionCardBase({ padding = "default", compact = false, flush = false, bodyStyle, styles, ...props }: SectionCardBaseProps) {
  const bodyPadding = flush ? 0 : padding === "compact" || compact ? spacing.md : spacing.lg;
  const merge = (resolved: CardProps["styles"]) => ({
    ...resolved, header: { borderBottom: "none", ...resolved?.header },
    body: { padding: bodyPadding, ...resolved?.body, ...bodyStyle },
  });
  return <Card {...props} size={compact ? "small" : props.size} styles={merge(styles)} />;
}

export interface SectionCardProps {
  /** Titel der Section. Bereits übersetzt übergeben (z. B. `tr("Overview")`). */
  title?: ReactNode;
  /** Rechtsbündiger Slot in der Card-Kopfzeile (Actions, Filter, Badges). */
  extra?: ReactNode;
  children?: ReactNode;
  /** Dichtere Variante (kleinere Kopfzeile + reduziertes Padding) → AntD `size="small"`. */
  compact?: boolean;
  /** Entfernt das Body-Padding (z. B. wenn der Body nur eine randlose Tabelle enthält). */
  flush?: boolean;
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Direkter Durchgriff auf `Card.styles` (selten nötig). */
  styles?: CardProps["styles"];
  /** Eindeutige ID / Test-Hook. */
  id?: string;
}

/**
 * Einheitliche Inhalts-Card für mQorva-Pages.
 *
 * Dünner Wrapper um die Kit-`Card`. Border, Radius, Kopf-/Body-Padding und
 * Titel-Typografie kommen zentral aus dem Gerüst (`ui/surface.css`, Familien in
 * `ui/families.css`), nicht aus Inline-Styles. Dadurch sehen alle Sections
 * projektübergreifend gleich aus.
 *
 * Ersetzt das wiederkehrende Muster
 * einer lokal gestylten Card mit eigenem Innen- und Außenabstand.
 * Den vertikalen Abstand übernimmt der umschließende `<PageContainer>`.
 *
 * Eine Grundkarte für alle Apps (Kopf ohne Trennlinie, 16 px Innenabstand, kompakt 12 px); die
 * früheren Kartensysteme `styles/*-card-system.css` sind entfallen (T4, 14.10.2026). Keine Produktweiche.
 */
export function SectionCard({
  title,
  extra,
  children,
  compact = false,
  flush = false,
  loading,
  className,
  style,
  styles,
  id,
}: SectionCardProps) {
  const classNames = ["mq-section-card"];
  if (flush) classNames.push("mq-card-flush");
  if (className) classNames.push(className);

  return (
    <Card
      id={id}
      title={title}
      extra={extra}
      loading={loading}
      size={compact ? "small" : "default"}
      variant="borderless"
      className={classNames.join(" ")}
      style={style}
      styles={styles}
    >
      {children}
    </Card>
  );
}
